import type { Injector, Signal } from '@angular/core';
import { Service } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { Router } from '@angular/router';
import type { TranslateService } from '@ngx-translate/core';
import type {
  OnyxLabelLink,
  OnyxMaybeArray,
  OnyxOptional,
  OnyxPaginated,
  OnyxPagination,
  OnyxTableView,
  OnyxTime,
  OnyxTimeRange,
  OnyxValues,
} from '@onyx/angular';
import {
  castArray,
  chain,
  isArray,
  isEqual,
  isFinite,
  isNaN,
  isString,
} from 'lodash';
import type { DateTimeUnit } from 'luxon';
import { DateTime, Duration } from 'luxon';
import { map, timer } from 'rxjs';
import type {
  Fleet,
  FleetParameters,
} from '../../dashboard/fleet/common/interfaces/fleet';
import { GanttQueryParamKey } from '../../dashboard/gantt/common/enums/gantt-query-param-key';
import { GanttRoute } from '../../dashboard/gantt/common/enums/gantt-route';
import { MapQueryParamKey } from '../../dashboard/map/common/enums/map-query-param-key';
import { MapRoute } from '../../dashboard/map/common/enums/map-route';
import { MapStore } from '../../dashboard/map/common/stores/map.store';
import { SwapStore } from '../../dashboard/map/common/stores/swap.store';
import type {
  Order,
  OrderParameters,
} from '../../dashboard/orders/common/interfaces/order';
import { ALPHABET } from '../constants/common/alphabet';
import {
  EUROPE_COUNTRIES,
  EU_COUNTRIES,
} from '../constants/common/country-groups';
import type { Country } from '../enums/country';
import { CountryGroup } from '../enums/country-group';
import { DictionaryCode } from '../enums/dictionary-code';
import type { VatRate } from '../enums/vat-rate';
import type { BusinessHours } from '../interfaces/common/business-hours';
import type { HelperOptions } from '../interfaces/utilities/helper-options';
import { TelematicsStore } from '../stores/telematics.store';

export interface Parameter {
  key: string;
  name: string;
  value: NonNullable<OnyxValues<FleetParameters> | OnyxValues<OrderParameters>>;
}

@Service()
export class CommonHelper {
  public readonly now = this.getDateTimeSignal('second');
  public readonly nowMinute = this.getDateTimeSignal('minute');
  public readonly nowHour = this.getDateTimeSignal('hour');
  public readonly nowDay = this.getDateTimeSignal('day');

  public readonly now$ = toObservable(this.now);
  public readonly nowMinute$ = toObservable(this.nowMinute);
  public readonly nowHour$ = toObservable(this.nowHour);
  public readonly nowDay$ = toObservable(this.nowDay);

  private getDateTimeSignal(unit: DateTimeUnit): Signal<DateTime> {
    const now = DateTime.now().startOf(unit);

    return toSignal(
      timer(
        now.plus({ [unit]: 1 }).toJSDate(),
        Duration.fromObject({ [unit]: 1 }).toMillis(),
      ).pipe(map(() => DateTime.now().startOf(unit))),
      { initialValue: now },
    );
  }

  public static getOpeningHours(
    date: string | DateTime,
    businessHours: OnyxOptional<BusinessHours>,
  ): OnyxTimeRange | null {
    if (!businessHours) return null;

    return chain(date)
      .thru((date) => (isString(date) ? DateTime.fromISO(date) : date))
      .thru((dateTime) => dateTime.weekday)
      .thru((weekday): OnyxTimeRange | null => {
        if (weekday === 7) return businessHours.sunday;
        if (weekday === 6) return businessHours.saturday;
        return businessHours.weekday;
      })
      .value();
  }

  public static getSortedParameters(
    parameters: Fleet['additionalParameters'] | Order['parameters'],
    translateService: TranslateService,
  ): Parameter[] {
    const CUSTOM_KEYS: Record<string, string> = {
      requiresDidb: 'didb',
      minEmissionStandard: 'emissionStandard',
    } as const;

    return chain(parameters)
      .entries()
      .map(([key, value]) => ({ key, value }))
      .filter(
        ({ value }) =>
          value != null &&
          value !== false &&
          (isArray(value) ? value.length !== 0 : true),
      )
      .map(({ key, value }) => ({
        key,
        name: `labels.${CUSTOM_KEYS[key] ?? key}`,
        value: value as NonNullable<typeof value>,
      }))
      .orderBy((parameter) =>
        translateService.instant(parameter.name).toLowerCase(),
      )
      .value();
  }

  public static getPreviewLink<T>(
    value: OnyxOptional<T>,
    callback: (value: T) => void,
  ): OnyxLabelLink | null {
    if (!value) return null;

    return {
      label: 'labels.preview',
      color: 'blue',
      font: 'f-body-xs f-medium',
      callback: () => callback(value),
    };
  }

  public static handleOptions(
    items: OnyxMaybeArray<{ uuid: string } | string>,
    options: HelperOptions,
  ): void {
    const itemsUuid = new Set(
      chain(items)
        .thru((items) => castArray(items))
        .map((item) => (isString(item) ? item : item.uuid))
        .value(),
    );

    options?.selectedItems?.update((items) =>
      items.filter(({ uuid }) => !itemsUuid.has(uuid)),
    );
    options?.close$?.next();
  }

  public static isNumeric(value: string): boolean {
    return !isNaN(parseFloat(value)) && !isFinite(value);
  }

  public static showOnMap(
    router: Router,
    uuid: string,
    injector: Injector,
    options?: {
      showRouteCalculator?: boolean;
      showService?: boolean;
      showSwapPanel?: boolean;
      orderUuid?: string;
    },
  ): void {
    const mapStore = injector.get(MapStore);
    mapStore.closeFeatures(injector);

    const { showRouteCalculator, showService, showSwapPanel, orderUuid } =
      options ?? {};

    const queryParams = {
      [MapQueryParamKey.VEHICLE_UUID]: uuid,
      ...(showRouteCalculator && { [MapQueryParamKey.ROUTE_CALCULATOR]: '' }),
      ...(showService && { [MapQueryParamKey.SERVICE]: '' }),
      ...(showSwapPanel && { [MapQueryParamKey.SWAP_PANEL]: '' }),
      ...(orderUuid && { [MapQueryParamKey.ORDER_UUID]: orderUuid }),
    };

    router.navigate([MapRoute.MAP], { queryParams });

    if (router.url.includes(MapRoute.MAP)) {
      if (showRouteCalculator) mapStore.toggleShowRouteCalculator();
      if (showService) mapStore.toggleShowService();
      if (showSwapPanel) injector.get(SwapStore).toggleShowSwapPanel();

      const vehicle = injector.get(TelematicsStore).getItem(uuid);
      if (!vehicle) return;

      mapStore.selectVehicle(vehicle);
      if (!vehicle.telemetry) mapStore.toggleShowInactive(true);
    }
  }

  public static showOnGantt(
    router: Router,
    type: 'vehicle' | 'order',
    uuid: string,
    date?: string,
  ): void {
    const route = router
      .createUrlTree([GanttRoute.GANTT], {
        queryParams: {
          [type === 'vehicle'
            ? GanttQueryParamKey.VEHICLE_UUID
            : GanttQueryParamKey.ORDER_UUID]: uuid,
          ...(date && { [GanttQueryParamKey.DATE]: date }),
        },
      })
      .toString();
    router.navigateByUrl(route);
  }

  public static toggleArray<T>(array: T[], value: OnyxMaybeArray<T>): T[] {
    const values = castArray(value);
    for (const value of values) {
      if (array.includes(value)) {
        array = array.filter((item) => item !== value);
      } else {
        array.push(value);
      }
    }
    return array;
  }

  public static getPaginatedData<T>(
    data: OnyxOptional<T[]>,
    pagination: OnyxPagination,
  ): OnyxPaginated<T> {
    const { page, limit } = pagination;
    const start = (page - 1) * limit;
    const end = page * limit;

    return {
      items: data?.slice(start, end) ?? [],
      totalItems: data?.length ?? 0,
      page,
      limit,
    };
  }

  public static isEnum<T extends Record<string, string>>(
    enumType: T,
    value: unknown,
  ): value is OnyxValues<T> {
    return Object.values(enumType).includes(value as OnyxValues<T>);
  }

  public static isSameDay(
    date: string | DateTime,
    compareDate: string | DateTime = DateTime.now(),
  ): boolean {
    const parsedDate = isString(date) ? DateTime.fromISO(date) : date;
    const parsedCompareDate = isString(compareDate)
      ? DateTime.fromISO(compareDate)
      : compareDate;
    return parsedCompareDate.hasSame(parsedDate, 'day');
  }

  public static addBusinessDays(
    date: DateTime | string,
    days: number,
  ): DateTime {
    date = isString(date) ? DateTime.fromISO(date) : date;
    if (days === 0) return date;

    while (days > 0) {
      date = date.plus({ days: 1 });
      if (date.weekday <= 5) --days;
    }

    return date;
  }

  public static getNextDayOfMonth(date: DateTime, day: number): DateTime {
    let target = date.set({ day });

    if (!target.isValid || target < date) {
      target = date.plus({ months: 1 }).set({ day });

      while (!target.isValid) {
        target = target.plus({ months: 1 }).set({ day });
      }
    }

    return target;
  }

  public static isPast(
    date: DateTime | string,
    compareDate: DateTime | string = DateTime.now(),
  ): boolean {
    const dateTime = isString(date) ? DateTime.fromISO(date) : date;
    const compareDateTime = isString(compareDate)
      ? DateTime.fromISO(compareDate)
      : compareDate;

    return dateTime.diff(compareDateTime).toMillis() < 0;
  }

  public static getAlphabetLetter(index: number): string {
    return ALPHABET[index % ALPHABET.length];
  }

  public static arrayEqualComparator = <T>(a: T, b: T): boolean | undefined => {
    if (!isArray(a) || !isArray(b)) return undefined;
    return a.length === b.length && isEqual(a.toSorted(), b.toSorted());
  };

  public static calculateVat(price: number, vatRate: VatRate): number {
    if (!CommonHelper.isNumeric(vatRate)) return 0;
    return price * (+vatRate / 100);
  }

  public static createDefaultTableView = <ListFiltersType, SortType>(view: {
    uuid: string;
    name: string;
    icon: string;
    columns: string[];
    filters: ListFiltersType;
    sort?: SortType;
    isSubView?: boolean;
  }): OnyxTableView<ListFiltersType, SortType> => ({
    uuid: view.uuid,
    name: view.name,
    value: {
      icon: view.icon,
      columns: view.columns,
      isDefault: true,
      sort: view.sort,
    },
    filters: view.filters,
    isSubView: view.isSubView ?? false,
  });

  public static extractDateTimeParts(value: OnyxOptional<DateTime | string>): {
    date: string | null;
    time: OnyxTime | null;
  } {
    if (value == null) return { date: null, time: null };

    const dateTime = isString(value) ? DateTime.fromISO(value) : value;
    return {
      date: dateTime.toISODate(),
      time: dateTime.toFormat('HH:mm:ss') as OnyxTime,
    };
  }

  public static getGroupedCountriesKeys(countries: Country[]): string[] {
    const COUNTRY_GROUPS = [
      { countries: new Set(EUROPE_COUNTRIES), key: CountryGroup.EUROPE },
      { countries: new Set(EU_COUNTRIES), key: CountryGroup.EU },
    ] as const;

    const whitelistSet = new Set(countries);
    const matchedGroup = COUNTRY_GROUPS.find((group) =>
      group.countries.isSubsetOf(whitelistSet),
    );
    if (!matchedGroup) {
      return countries.map((country) => `${DictionaryCode.COUNTRY}.${country}`);
    }

    const groupName = `${DictionaryCode.COUNTRIES_GROUP}.${matchedGroup.key}`;
    const additionalCountries = whitelistSet
      .difference(matchedGroup.countries)
      .values()
      .map((country) => `${DictionaryCode.COUNTRY}.${country}`)
      .toArray();

    return [groupName, ...additionalCountries];
  }
}
